/**
 * Import wizard (§8.1, Phase 4 backend): Stage → review per-section diff →
 * Commit (SuperAdmin, re-verifies the reviewed stageHash server-side).
 */

import React, { useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Flex,
  Text,
  useToast,
} from '@chakra-ui/react';

const SECTIONS = [
  { key: 'profiles', label: 'LO profiles', itemKey: 'email' },
  { key: 'margins', label: 'Margins', itemKey: 'lender' },
  { key: 'loa', label: 'LOA fees', itemKey: 'secondary' },
  { key: 'splits', label: 'Split rules', itemKey: 'rule' },
  { key: 'referrals', label: 'Referral rules', itemKey: 'rule' },
];

function DiffLine({ item, itemKey }) {
  return (
    <Box py={1} borderBottomWidth="1px" _last={{ borderBottomWidth: 0 }}>
      <Text fontSize="xs">
        <Badge mr={2} colorScheme={item.status === 'new' ? 'green' : 'orange'}>{item.status}</Badge>
        <b>{item[itemKey]}</b>
      </Text>
      {(item.changes || []).map((c) => (
        <Code key={c.field} fontSize="xs" ml={6} mr={1}>
          {c.field}: {String(c.db ?? '—')} → {String(c.staged ?? '—')}
        </Code>
      ))}
      {(item.warnings || []).map((w) => (
        <Text key={w} fontSize="xs" color="orange.400" ml={6}>⚠ {w}</Text>
      ))}
    </Box>
  );
}

export default function ImportWizard({ isSuperAdmin, payrollApi, onCommitted }) {
  const toast = useToast();
  const [staged, setStaged] = useState(null); // { stageHash, diff, at }
  const [staging, setStaging] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null); // { counts }

  const stage = async () => {
    setStaging(true);
    setCommitted(null);
    try {
      const { stageHash, diff } = await payrollApi.importReference({ action: 'stage' });
      setStaged({ stageHash, diff, at: new Date() });
    } catch (err) {
      toast({ title: 'Stage failed', description: err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setStaging(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const res = await payrollApi.importReference({ action: 'commit', stageHash: staged.stageHash });
      setCommitted(res);
      setStaged(null);
      toast({ title: 'Import committed', status: 'success', duration: 5000 });
      if (onCommitted) onCommitted();
    } catch (err) {
      toast({ title: 'Commit failed', description: err.message, status: 'error', duration: 10000, isClosable: true });
    } finally {
      setCommitting(false);
    }
  };

  const pendingChanges = staged
    ? SECTIONS.reduce((n, s) => n + (staged.diff[s.key] || []).filter((i) => i.status !== 'unchanged').length, 0)
    : 0;

  return (
    <Box>
      <Text fontSize="sm" color="gray.500" mb={3}>
        Re-imports the legacy reference spreadsheet (Comp &amp; Margins &amp; LOA tabs) into the Hub tables.
        Stage reads the sheet and shows what would change — nothing is written until a SuperAdmin commits
        the exact staged version. Normal edits should use the Reference tab instead; this exists for
        bulk drift reconciliation while the sheet is still authoritative.
      </Text>

      <Flex gap={2} mb={4}>
        <Button size="sm" colorScheme="purple" onClick={stage} isLoading={staging} loadingText="Reading sheet…">
          Stage import (read-only)
        </Button>
        {staged && (
          <Button
            size="sm"
            colorScheme="red"
            onClick={commit}
            isLoading={committing}
            isDisabled={!isSuperAdmin}
            title={isSuperAdmin ? undefined : 'SuperAdmin only'}
          >
            Commit this staged import
          </Button>
        )}
      </Flex>

      {committed && (
        <Alert status="success" borderRadius="md" mb={3} fontSize="sm">
          <AlertIcon />
          Committed — {Object.entries(committed.counts || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}
        </Alert>
      )}

      {staged && (
        <>
          <Alert status={pendingChanges ? 'warning' : 'info'} borderRadius="md" mb={3} fontSize="sm">
            <AlertIcon />
            <Box>
              <Text>
                Staged at {staged.at.toLocaleTimeString()} — {pendingChanges} pending change{pendingChanges === 1 ? '' : 's'}.
                Commit verifies the sheet still hashes to <Code fontSize="xs">{staged.stageHash.slice(0, 12)}…</Code>
              </Text>
              {!isSuperAdmin && <Text mt={1}>You can review, but committing requires a SuperAdmin.</Text>}
            </Box>
          </Alert>

          <Accordion allowMultiple defaultIndex={SECTIONS.map((s, i) => ((staged.diff[s.key] || []).some((x) => x.status !== 'unchanged') ? i : -1)).filter((i) => i >= 0)}>
            {SECTIONS.map((s) => {
              const items = staged.diff[s.key] || [];
              const changed = items.filter((i) => i.status !== 'unchanged');
              const counts = (staged.diff.counts && staged.diff.counts[s.key]) || {};
              return (
                <AccordionItem key={s.key}>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontSize="sm">
                      <b>{s.label}</b>{' '}
                      <Badge ml={2} colorScheme={counts.new ? 'green' : 'gray'}>{counts.new || 0} new</Badge>{' '}
                      <Badge colorScheme={counts.changed ? 'orange' : 'gray'}>{counts.changed || 0} changed</Badge>{' '}
                      {counts.unchanged !== undefined && <Badge>{counts.unchanged} unchanged</Badge>}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={3}>
                    {changed.length === 0 && <Text fontSize="sm" color="green.500">No changes.</Text>}
                    {changed.map((item) => <DiffLine key={item[s.itemKey]} item={item} itemKey={s.itemKey} />)}
                    {s.key === 'profiles' && (staged.diff.profilesDbOnly || []).length > 0 && (
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        In Hub but not on the sheet (untouched): {staged.diff.profilesDbOnly.join(', ')}
                      </Text>
                    )}
                    {s.key === 'margins' && (staged.diff.marginWarnings || []).length > 0 && (
                      <Text fontSize="xs" color="orange.400" mt={2}>{staged.diff.marginWarnings.join(' · ')}</Text>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        </>
      )}
    </Box>
  );
}
